import { type ReactNode, useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Icon, IconButton, Toast, type IconName } from '@/design-system';
import type { ToastMsg } from '@/design-system/components';
import { Logo } from './Logo';
import { HouseholdSwitcher } from './HouseholdSwitcher';
import { CommandBar } from './CommandBar';
import { BugReporter } from './BugReporter';
import { PlanBadge } from './UpgradeGate';
import { usePrefs, useSession, useToasts } from '@/lib/store';
import { Auth, Tenants } from '@/lib/api/v1';
import { clsx } from 'clsx';
import { ThemeToggle } from './ThemeToggle';
import { NAV_CAPABILITY_BY_PATH, minPlanFor, resolveEntitlements } from '@/lib/entitlements';

type NavItem = { to: string; icon: IconName; key: keyof typeof navLabel };
type NavGroup = { key: 'primary' | 'discover' | 'plan' | 'social' | 'tools'; items: NavItem[] };

const navGroups: NavGroup[] = [
  {
    key: 'primary',
    items: [
      { to: '/cellar', icon: 'bottle', key: 'cellar' },
      { to: '/scan', icon: 'scan', key: 'scan' },
    ],
  },
  {
    key: 'discover',
    items: [
      { to: '/nyheter', icon: 'sparkles', key: 'nyheter' },
      { to: '/utforsk', icon: 'search', key: 'utforsk' },
      { to: '/populaer', icon: 'star', key: 'popular' },
      { to: '/wishlist', icon: 'heart', key: 'wishlist' },
    ],
  },
  {
    key: 'plan',
    items: [
      { to: '/pair', icon: 'chef-hat', key: 'pair' },
      { to: '/occasions', icon: 'utensils', key: 'occasions' },
      { to: '/recommend', icon: 'sparkles', key: 'recommend' },
    ],
  },
  {
    key: 'social',
    items: [
      { to: '/feed', icon: 'check-circle', key: 'feed' },
      { to: '/friends', icon: 'users', key: 'friends' },
      { to: '/stats', icon: 'trend-up', key: 'stats' },
    ],
  },
  {
    key: 'tools',
    items: [
      { to: '/guides', icon: 'chef-hat', key: 'guides' },
      { to: '/labels', icon: 'tag', key: 'labels' },
      { to: '/vinliste', icon: 'list', key: 'vinliste' },
      { to: '/prising', icon: 'receipt', key: 'pricing' },
      { to: '/bestilling', icon: 'truck', key: 'bestilling' },
      { to: '/varetelling', icon: 'archive', key: 'varetelling' },
      { to: '/pos', icon: 'external', key: 'pos' },
      { to: '/profil', icon: 'globe', key: 'profil' },
      { to: '/historikk', icon: 'clock', key: 'historikk' },
      { to: '/abonnement', icon: 'sparkles', key: 'abonnement' },
      { to: '/settings', icon: 'settings', key: 'settings' },
    ],
  },
];

const mobileTabs = [
  { to: '/cellar', icon: 'bottle' as const, key: 'cellar' as const },
  { to: '/scan', icon: 'scan' as const, key: 'scan' as const },
  { to: '/pair', icon: 'chef-hat' as const, key: 'pair' as const },
  { to: '/settings', icon: 'settings' as const, key: 'settings' as const },
];

const navLabel: Record<string, string> = {
  cellar: 'cellar',
  scan: 'scan',
  nyheter: 'nyheter',
  utforsk: 'utforsk',
  popular: 'popular',
  wishlist: 'wishlist',
  feed: 'feed',
  friends: 'friends',
  stats: 'stats',
  guides: 'guides',
  racks: 'racks',
  occasions: 'occasions',
  pair: 'pair',
  recommend: 'recommend',
  labels: 'labels',
  vinliste: 'vinliste',
  pricing: 'pricing',
  bestilling: 'bestilling',
  varetelling: 'varetelling',
  pos: 'pos',
  profil: 'profil',
  historikk: 'historikk',
  abonnement: 'abonnement',
  settings: 'settings',
};

export function AppShell({ children }: { children?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toasts, dismiss } = useToasts();
  const { user, signOut } = useSession();
  const { activeTenantId, setActiveTenantId } = usePrefs();
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const [cmdOpen, setCmdOpen] = useState(false);

  // Nav plan badges are a UI hint only (see lib/entitlements.ts doc
  // comment) — enforcement always happens server-side. Query is cheap and
  // cached (tenants rarely change plan mid-session); stale badge for a few
  // seconds after an upgrade is an acceptable tradeoff for not adding a
  // dedicated endpoint just for this.
  const tenantsQ = useQuery({
    queryKey: ['tenants'],
    queryFn: () => Tenants.list(),
    enabled: Boolean(user),
    staleTime: 60_000,
  });
  const tenants = tenantsQ.data?.tenants ?? [];
  const activeTenant = tenants.find((t) => t.id === activeTenantId) ?? tenants[0];
  const capabilities = activeTenant ? resolveEntitlements(activeTenant) : undefined;

  // Cmd-K / Ctrl-K to open command bar
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (isLanding) {
    return (
      <div className="relative min-h-screen">
        <Outlet />
        <ToastStack toasts={toasts} onDismiss={dismiss} />
      </div>
    );
  }

  const handleSignOut = async () => {
    try {
      await Auth.logout();
    } catch {
      /* ignore */
    } finally {
      signOut();
      setActiveTenantId(null);
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="relative grid min-h-screen grid-cols-1 md:grid-cols-[5.5rem_1fr] xl:grid-cols-[var(--ui-sidebar-w,276px)_1fr]">
      {/* Top nav (mobile) */}
      <header className="ui-nav md:hidden">
        <div className="flex h-16 items-center justify-between px-4">
          <NavLink to="/cellar"><Logo size={28} /></NavLink>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setCmdOpen(true)} className="ui-btn ui-btn--ghost ui-btn--sm" aria-label="Søk">
              <Icon name="search" size={14} />
            </button>
            <ThemeToggle />
            <button
              type="button"
              onClick={() => i18n.changeLanguage(i18n.language.startsWith('nb') ? 'en' : 'nb')}
              className="ui-pill"
            >
              <Icon name="globe" size={12} />
              {i18n.language.toUpperCase().slice(0, 2)}
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar (tablet + desktop) */}
      <aside className="sticky top-0 hidden h-screen flex-col border-r border-line bg-ink-soft/60 backdrop-blur md:flex">
        <div className="px-3 py-5 xl:px-6 xl:py-6">
          <NavLink to="/cellar" className="flex justify-center xl:block" aria-label="Alkis">
            <Logo size={34} showWordmark={false} className="xl:hidden" />
            <Logo size={34} className="hidden xl:inline-flex" />
          </NavLink>
          <p className="mt-2 hidden max-w-[190px] text-xs uppercase tracking-widest text-mist xl:block">{t('app.tagline')}</p>
        </div>

        <div className="hidden px-4 pb-3 xl:block">
          <HouseholdSwitcher />
        </div>

        <div className="px-3 pb-2 xl:px-4">
          <button
            type="button"
            onClick={() => setCmdOpen(true)}
            className="ui-input flex w-full items-center justify-center gap-2 text-left text-sm text-mist xl:justify-start"
            aria-label="Søk"
          >
            <Icon name="search" size={14} />
            <span className="hidden xl:inline">Søk...</span>
            <kbd className="ml-auto hidden text-[10px] xl:inline">⌘K</kbd>
          </button>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 xl:px-4">
          <div className="grid gap-3 xl:gap-4">
            {navGroups.map((group) => (
              <div key={group.key} className="grid gap-1">
                {group.key !== 'primary' && (
                  <div className="hidden px-3 pt-1 text-[10px] font-medium uppercase tracking-widest text-mist/75 xl:block">
                    {t(`navGroups.${group.key}`)}
                  </div>
                )}
                {group.items.map((l) => {
                  const requiredCapability = NAV_CAPABILITY_BY_PATH[l.to];
                  const locked = Boolean(requiredCapability && capabilities && !capabilities[requiredCapability]);
                  return (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      title={t(`nav.${navLabel[l.key]}`)}
                      aria-label={t(`nav.${navLabel[l.key]}`)}
                      className={({ isActive }) =>
                        clsx(
                          'flex items-center justify-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all xl:justify-start xl:py-2',
                          isActive
                            ? 'bg-candle/10 text-candle shadow-glow'
                            : 'text-mist hover:bg-overlay-soft hover:text-parchment',
                        )
                      }
                    >
                      <Icon name={l.icon} size={17} />
                      <span className="hidden font-medium xl:inline">{t(`nav.${navLabel[l.key]}`)}</span>
                      {locked && requiredCapability && (
                        <span className="hidden xl:inline">
                          <PlanBadge plan={minPlanFor(requiredCapability)} />
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </div>
        </nav>
        <div className="mt-auto p-4">
          <div className="ui-card flex items-center justify-center gap-3 p-2 xl:justify-start xl:p-3">
            <div className="grid h-9 w-9 place-items-center rounded-full bg-candle/10 text-candle">
              <Icon name="user" size={16} />
            </div>
            <div className="hidden min-w-0 xl:grid">
              <span className="truncate text-xs font-medium text-parchment max-w-[140px]">
                {user?.email ?? 'demo@vin.invested.no'}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-mist">
                {user?.displayName ?? 'Dev'}
              </span>
            </div>
            <ThemeToggle className="hidden !py-1 !px-2 xl:ml-auto xl:inline-flex" />
            <button
              type="button"
              onClick={() => i18n.changeLanguage(i18n.language.startsWith('nb') ? 'en' : 'nb')}
              className="hidden rounded px-2 py-1 text-xs uppercase tracking-wider text-mist hover:text-candle xl:inline"
              aria-label="Toggle language"
            >
              {i18n.language.startsWith('nb') ? 'EN' : 'NB'}
            </button>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-xs uppercase tracking-wider text-mist hover:text-danger xl:justify-start"
            aria-label={t('nav.signOut')}
          >
            <Icon name="logout" size={12} /> <span className="hidden xl:inline">{t('nav.signOut')}</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main
        className="relative z-10 min-h-screen px-4 pt-4 sm:px-6 sm:pt-6 md:px-7 md:pt-7 lg:px-8 xl:px-12 xl:pt-10"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
      >
        {children ?? <Outlet />}
      </main>

      {/* Mobile bottom tab bar — respects iOS safe-area */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 grid grid-cols-4 gap-1 border-t border-line bg-ink-soft/85 px-2 backdrop-blur md:hidden"
        style={{ paddingTop: '0.5rem', paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        {mobileTabs.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            className={({ isActive }) =>
              clsx(
                'grid place-items-center rounded-md py-2 text-[10px] uppercase tracking-wider',
                isActive ? 'text-candle' : 'text-mist',
              )
            }
          >
            <Icon name={l.icon} size={20} />
            <span className="mt-1 text-[10px]">{t(`nav.${navLabel[l.key]}`)}</span>
          </NavLink>
        ))}
      </nav>

      <CommandBar open={cmdOpen} onClose={() => setCmdOpen(false)} />
      <BugReporter />
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}

function ToastStack({ toasts, onDismiss }: { toasts: ToastMsg[]; onDismiss: (id: string) => void }) {
  return (
    <div className="fixed bottom-20 right-4 z-50 grid w-full max-w-sm gap-2 md:bottom-6">
      {toasts.map((t) => (
        <Toast key={t.id} msg={t} onClose={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}
